/**
 * Constantes de los tableros del módulo LCL.
 *
 * Los `estado` (fase principal) son los mismos cuatro del aéreo para
 * que el kanban se comporte igual; los `subEstado` sí son marítimos
 * (Booking, Consolidación, Zarpe…) porque describen el ciclo de una
 * operación LCL.
 */

/** Fases principales. Coinciden con el enum de `LclRequest.estado`. */
export const ESTADOS = ['Borrador', 'Enviada', 'Aprobada', 'Rechazada'];

export const ESTADO_BADGE = {
  Borrador: 'bg-amber-100 text-amber-800 border-amber-200',
  Enviada: 'bg-blue-100 text-blue-800 border-blue-200',
  Aprobada: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Rechazada: 'bg-rose-100 text-rose-800 border-rose-200',
};

/** Ruta del tablero detallado de cada fase. */
export const BOARD_ROUTE_BY_ESTADO = {
  Borrador: '/lcl/board/cotizaciones',
  Enviada: '/lcl/board/en-curso',
  Aprobada: '/lcl/board/finalizadas',
  Rechazada: '/lcl/board/rechazadas',
};

/** Filas por página en los tableros. */
export const ITEMS_PER_PAGE = 10;

/** Días sin respuesta tras los cuales una cotización se marca "Vencida". */
export const VENCIDA_DIAS = 7;

/**
 * Sub-estados por tablero. Cada tablero define los suyos; el backend
 * no los valida (`subEstado` es String libre en el schema).
 */
export const SUBESTADOS_COTIZACIONES = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Enviada', label: 'Enviada' },
  { value: 'Esperando respuesta', label: 'Esperando respuesta' },
  { value: 'Vencida', label: 'Vencida' },
];

export const SUBESTADOS_EN_CURSO = [
  { value: 'Booking', label: 'Booking' },
  { value: 'Consolidación', label: 'Consolidación' },
  { value: 'En tránsito', label: 'En tránsito' },
  { value: 'En aduana', label: 'En aduana' },
];

export const SUBESTADOS_FINALIZADAS = [
  { value: 'Cerrada', label: 'Cerrada' },
  { value: 'Archivada', label: 'Archivada' },
];

export const SUBESTADOS_RECHAZADAS = [
  { value: 'Cancelada por cliente', label: 'Cancelada por cliente' },
  { value: 'Anulada por operador', label: 'Anulada por operador' },
];

/** Clases del badge de cada sub-estado, de los cuatro tableros. */
export const SUBESTADO_BADGE = {
  // Cotizaciones
  Borrador: 'bg-slate-100 text-slate-700 border-slate-200',
  Enviada: 'bg-blue-100 text-blue-700 border-blue-200',
  'Esperando respuesta': 'bg-amber-100 text-amber-800 border-amber-200',
  Vencida: 'bg-rose-100 text-rose-700 border-rose-200',
  // En curso
  Booking: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  Consolidación: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'En tránsito': 'bg-blue-100 text-blue-700 border-blue-200',
  'En aduana': 'bg-amber-100 text-amber-800 border-amber-200',
  // Finalizadas
  Cerrada: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Archivada: 'bg-slate-100 text-slate-700 border-slate-200',
  // Rechazadas
  'Cancelada por cliente': 'bg-rose-100 text-rose-700 border-rose-200',
  'Anulada por operador': 'bg-orange-100 text-orange-700 border-orange-200',
};

/** Color del borde de la tarjeta métrica de cada sub-estado. */
export const SUBESTADO_METRIC_COLOR = {
  Borrador: 'border-slate-300 text-slate-700',
  Enviada: 'border-blue-300 text-blue-700',
  'Esperando respuesta': 'border-amber-300 text-amber-700',
  Vencida: 'border-rose-300 text-rose-700',
  Booking: 'border-indigo-300 text-indigo-700',
  Consolidación: 'border-cyan-300 text-cyan-700',
  'En tránsito': 'border-blue-300 text-blue-700',
  'En aduana': 'border-amber-300 text-amber-700',
  Cerrada: 'border-emerald-300 text-emerald-700',
  Archivada: 'border-slate-300 text-slate-700',
  'Cancelada por cliente': 'border-rose-300 text-rose-700',
  'Anulada por operador': 'border-orange-300 text-orange-700',
};

/** Tipos de servicio. Coincide con el enum de `LclRequest.tipoServicio`. */
export const TIPOS_SERVICIO = [
  'Puerto a Puerto',
  'Puerta a Puerto',
  'Puerto a Puerta',
  'Puerta a Puerta',
];

/** Tipos de bulto. Coincide con el enum de `LclPackageDetail.tipoBulto`. */
export const TIPOS_BULTO = ['Caja', 'Pallet', 'Huacal', 'Saco', 'Bulto', 'Otro'];

/**
 * Unidades de dimensión. Coincide con el enum de
 * `LclPackageDetail.unidadDimension` y con `UNIDADES_DIMENSION_LCL`
 * del DTO. En marítimo los pallets suelen medirse en metros.
 */
export const UNIDADES_DIMENSION = [
  { value: 'cm', label: 'cm' },
  { value: 'm', label: 'm' },
  { value: 'in', label: 'in' },
];

/** Unidades de peso. Coincide con `LclPackageDetail.unidadPeso`. */
export const UNIDADES_PESO = [
  { value: 'kg', label: 'kg' },
  { value: 'lb', label: 'lb' },
];

/** Monedas del valor de la mercancía. Debe coincidir con el DTO. */
export const MONEDAS_VALOR_MERCANCIA = ['USD', 'COP', 'EUR', 'MXN'];

/** Código de incoterm que obliga a pedir la dirección de recogida. */
export const INCOTERM_CON_RECOGIDA = 'EXW';

/** ¿El servicio incluye recolección en la puerta del cliente en origen? */
export const requierePickup = (tipoServicio) =>
  String(tipoServicio || '').startsWith('Puerta');

/** ¿El servicio incluye entrega en la puerta del consignatario en destino? */
export const requiereDelivery = (tipoServicio) =>
  String(tipoServicio || '').endsWith('Puerta');
